import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowRight, Phone, Globe, Clock, Shield, CreditCard, Wifi } from "lucide-react";
import { MARKETING_CONFIG } from "@/config/marketing";
import { HeroCountrySearch } from "@/components/connect/HeroCountrySearch";
import { PopularCountryChips } from "@/components/connect/PopularCountryChips";
import { ConnectFooter } from "@/components/connect/ConnectFooter";
import { Logo } from "@/components/ui/Logo";
import { DialerLanguageToggle } from "@/components/connect/DialerLanguageToggle";
import { CurrencySwitcher } from "@/components/connect/CurrencySwitcher";
import { SEOHead } from "@/components/seo/SEOHead";
import { PAGE_SEO, getCanonicalUrl, ORGANIZATION_SCHEMA, WEBSITE_SCHEMA, TELECOM_SERVICE_SCHEMA } from "@/config/seo";
import { useCallerCountry } from "@/hooks/useCallerCountry";

export default function CallHome() {
  const { formatLocalAmount } = useCallerCountry();

  const STEPS = [
    { step: "1", title: "Sign up free", desc: "30 seconds. Just your phone number - no card needed.", icon: Shield },
    { step: "2", title: "Add credit", desc: `From ${formatLocalAmount(500)}. Pay with card or Apple/Google Pay. Purchased credit does not expire.`, icon: CreditCard },
    { step: "3", title: "Dial any number", desc: "Mobile or landline, in any country. The other side just picks up - no app needed.", icon: Phone },
  ];

  const FAQ_ITEMS = [
    { q: "Why elefa instead of WhatsApp or Signal?", a: "WhatsApp and Signal only work when both sides are on the app. elefa rings real phone numbers, so you can reach the people who don't - landlines, basic phones, anywhere." },
    { q: "How much does it cost?", a: `Rates start from ${MARKETING_CONFIG.lowestInternationalRate}/min depending on the country. You see the per-minute rate before every call.` },
    { q: "Does my credit expire?", a: "Purchased credit never expires while your account is active. Promotional credit (like your sign-up bonus) may expire. The date is shown when it's added and in your wallet." },
    { q: "What do I need to make calls?", a: "A smartphone or computer with internet. Works over WiFi, 4G, or 5G - no SIM card needed on your side. The person you call doesn't need internet at all." },
    { q: "Can I call landlines and mobiles?", a: "Yes. Any phone number, in any country." },
    { q: "Is it secure?", a: "All traffic between your device and our servers is encrypted in transit (TLS for signalling, SRTP for voice). Calls terminate on the public phone network (PSTN), which cannot be end-to-end encrypted by its nature. In-app elefa-to-elefa calling and messaging are not currently offered - they are on our future roadmap. elefa is the trading name of ELEFA MOBILE PTY LTD (ABN 16 694 346 088), operated from Australia; we are not currently a member of the Telecommunications Industry Ombudsman scheme." },
  ];

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title={PAGE_SEO.callHome.title}
        description={PAGE_SEO.callHome.description}
        keywords={PAGE_SEO.callHome.keywords}
        canonicalUrl={getCanonicalUrl("/call-home")}
        structuredData={[
          ORGANIZATION_SCHEMA,
          WEBSITE_SCHEMA,
          TELECOM_SERVICE_SCHEMA,
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: FAQ_ITEMS.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          },
        ]}
      />

      {/* Nav */}
      <nav className="container py-4 sm:py-6 flex flex-wrap items-center justify-between gap-2">
        <Link to="/" className="flex items-center gap-2">
          <Logo variant="header" size="md" asLink={false} withIcon fullName />
          <span className="text-xs bg-secondary text-secondary-foreground px-2 py-0.5 rounded-full font-medium inline-flex items-center translate-y-0.5">Connect</span>
        </Link>
        <div className="flex flex-wrap items-center justify-end gap-2 sm:gap-3">
          <CurrencySwitcher />
          <DialerLanguageToggle />
          <Link to="/connect-portal/auth"><Button variant="ghost" size="sm">Log In</Button></Link>
          <Link to="/connect-portal/auth"><Button size="sm">Get Started</Button></Link>
        </div>
      </nav>

      {/* Hero */}
      <section className="py-16 sm:py-24 flex flex-col items-center justify-center px-4">
        <div className="max-w-4xl mx-auto text-center space-y-6">
          <Badge variant="secondary" className="text-sm">Any phone · Any country · Purchased credit never expires</Badge>
          <h1 className="text-4xl sm:text-6xl md:text-7xl font-bold tracking-tight leading-tight">
            Call Home.<br />Wherever Home Is.
          </h1>
          <p className="text-xl md:text-2xl text-muted-foreground max-w-2xl mx-auto">
            Your family doesn't need internet to receive your call.
            elefa dials any phone, any number, in 195 countries. No app on their end. No Wi-Fi. Just their number.
          </p>
          <div className="flex justify-center pt-4">
            <HeroCountrySearch />
          </div>
          <div className="flex justify-center pt-2">
            <PopularCountryChips />
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 border-t bg-muted/30">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12">Get started in 3 steps</h2>
          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {STEPS.map((s) => (
              <div key={s.step} className="text-center space-y-4">
                <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
                  <s.icon className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-xl font-semibold">{s.title}</h3>
                <p className="text-muted-foreground">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Signals */}
      <section className="py-16 border-t">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto text-center">
            <div className="space-y-2">
              <Phone className="h-8 w-8 mx-auto text-primary" />
              <p className="font-medium">Clear voice quality</p>
              <p className="text-sm text-muted-foreground">Clear voice over Wi-Fi or mobile data</p>
            </div>
            <div className="space-y-2">
              <Globe className="h-8 w-8 mx-auto text-primary" />
              <p className="font-medium">Worldwide Reach</p>
              <p className="text-sm text-muted-foreground">Call any phone number worldwide</p>
            </div>
            <div className="space-y-2">
              <Clock className="h-8 w-8 mx-auto text-primary" />
              <p className="font-medium">Purchased Credit Never Expires</p>
              <p className="text-sm text-muted-foreground">Use your balance whenever you need</p>
            </div>
            <div className="space-y-2">
              <Wifi className="h-8 w-8 mx-auto text-primary" />
              <p className="font-medium">Any device</p>
              <p className="text-sm text-muted-foreground">Any device with internet access</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 border-t bg-muted/30">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12">Frequently Asked Questions</h2>
          <div className="max-w-2xl mx-auto space-y-6">
            {FAQ_ITEMS.map((f, i) => (
              <details key={i} className="group border rounded-lg bg-background">
                <summary className="cursor-pointer px-6 py-4 font-medium text-foreground flex items-center justify-between">
                  {f.q}
                  <ArrowRight className="h-4 w-4 text-muted-foreground group-open:rotate-90 transition-transform" />
                </summary>
                <p className="px-6 pb-4 text-muted-foreground">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 border-t bg-primary/5">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Ready to Call Home?</h2>
          <p className="text-lg text-muted-foreground mb-8">Sign up free and get calling in under a minute.</p>
          <Link to="/connect-portal/auth">
            <Button size="lg" className="gap-2">Get Started Free <ArrowRight className="h-4 w-4" /></Button>
          </Link>
        </div>
      </section>

      {/* Emergency Disclaimer */}
      <div className="container mx-auto px-4 py-6">
        <div className="text-xs text-center text-muted-foreground max-w-lg mx-auto">
          <span className="text-destructive font-semibold">Important:</span> elefa Connect is an internet-based calling service and cannot be used for emergency calls. Always have access to a traditional phone for emergencies.
        </div>
      </div>

      <ConnectFooter />
    </div>
  );
}
